import { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter, Navigate, Route, Routes,
} from "react-router-dom";
import { api, type Me } from "./api";
import { Layout } from "./Layout";
import { Login } from "./pages/Login";
import { Meetings } from "./pages/Meetings";
import { MeetingDetailPage } from "./pages/MeetingDetail";
import { Clients, ClientDetailPage } from "./pages/Clients";
import { MeetingTypePage } from "./pages/MeetingType";
import { Users } from "./pages/Users";
import { Account } from "./pages/Account";
import { SetPassword } from "./pages/SetPassword";

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/auth/me", { credentials: "same-origin" });
      setMe(res.ok ? ((await res.json()) as Me) : null);
    } catch {
      setMe(null);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const signOut = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    await refresh();
  }, [refresh]);

  // Invite and reset links work whether or not anyone is signed in here.
  if (window.location.pathname === "/set-password") return <SetPassword />;

  // Render nothing until the session check settles, so the login form never
  // flashes in front of someone who is already signed in.
  if (!checked) return null;
  if (!me) return <Login onSignedIn={() => void refresh()} />;

  const isAdmin = me.role === "admin";

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout me={me} onSignOut={() => void signOut()} />}>
          <Route index element={<Navigate to="/meetings" replace />} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/meetings/:id" element={<MeetingDetailPage isAdmin={isAdmin} />} />
          <Route path="/clients" element={<Clients isAdmin={isAdmin} />} />
          <Route path="/clients/:id" element={<ClientDetailPage isAdmin={isAdmin} />} />
          <Route
            path="/meeting-types/:id"
            element={isAdmin ? <MeetingTypePage /> : <Navigate to="/clients" replace />}
          />
          <Route
            path="/users"
            element={isAdmin ? <Users meId={me.id} /> : <Navigate to="/meetings" replace />}
          />
          <Route path="/account" element={<Account me={me} />} />
          <Route path="*" element={<Navigate to="/meetings" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
